class ProductCarousel extends HTMLElement {
  #activeIndex  = 0;
  #animating    = false;
  /** @type {{ realIndex: number; destroy(deleteInstance?: boolean, cleanStyles?: boolean): void; } | null} */
  #mobileSwiper = null;

  connectedCallback() {
    this.#bindThumbs();
    this.#activateBg(0);
    this.#initMobileSwiper();
  }

  disconnectedCallback() {
    const wrapper = /** @type {HTMLElement | null} */ (this.querySelector('[data-mobile-swiper] .swiper-wrapper'));
    wrapper?.querySelectorAll('.slide-clone').forEach(el => el.remove());
    this.#mobileSwiper?.destroy(true, true);
    this.#mobileSwiper = null;
  }

  #bindThumbs() {
    const thumbButtons = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-thumb]'));
    thumbButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const toIndex = parseInt(btn.dataset.thumb || '', 10);
        if (Number.isNaN(toIndex)) return;
        if (toIndex === this.#activeIndex || this.#animating) return;
        this.#transition(this.#activeIndex, toIndex);
      });
    });
  }

  // ─── Mobile Swiper ────────────────────────────────────────────────────────

  #initMobileSwiper() {
    const container = /** @type {HTMLElement | null} */ (this.querySelector('[data-mobile-swiper]'));
    if (!container || typeof Swiper === 'undefined') return;

    const wrapper = /** @type {HTMLElement | null} */ (container.querySelector('.swiper-wrapper'));
    if (!wrapper) return;

    // Remove stale clones from a previous init
    wrapper.querySelectorAll('.slide-clone').forEach(el => el.remove());

    const origSlides = Array.from(wrapper.querySelectorAll('.swiper-slide'));
    const origCount  = origSlides.length;
    if (origCount === 0) return;

    // Swiper loop needs enough slides to fill the view on both sides.
    // With slidesPerView ~1.4 and ≤4 real slides we clone the set twice.
    if (origCount > 1 && origCount <= 4) {
      for (let i = 0; i < 2; i++) {
        origSlides.forEach(slide => {
          const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
          clone.classList.add('slide-clone');
          wrapper.appendChild(clone);
        });
      }
    }

    const totalSlides = wrapper.querySelectorAll('.swiper-slide').length;
    const shouldLoop  = origCount > 1;

    this.#mobileSwiper = new Swiper(container, {
      loop:            shouldLoop,
      ...(shouldLoop ? { loopedSlides: totalSlides } : {}),
      centeredSlides:  true,
      slidesPerView:   1.758,
      spaceBetween:    20,
      speed:           400,
      grabCursor:      true,
      watchOverflow:   true,
      observer:        true,
      observeParents:  true,
      on: {
        realIndexChange: (/** @type {{ realIndex: number }} */ swiper) => {
          this.#updateMobPanel(swiper.realIndex % origCount);
        },
      },
    });
  }

  #updateMobPanel(/** @type {number} */ index) {
    const mobPanels = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-mob-panel]'));
    mobPanels.forEach((panel) => {
      const i = parseInt(panel.dataset.mobPanel || '', 10);
      panel.classList.toggle('is-active', i === index);
    });
  }

  // ─── Core desktop transition ──────────────────────────────────────────────

  #transition(/** @type {number} */ fromIndex, /** @type {number} */ toIndex) {
    this.#animating = true;

    const outgoing = /** @type {HTMLElement | null} */ (this.querySelector(`[data-product="${fromIndex}"]`));
    const incoming = /** @type {HTMLElement | null} */ (this.querySelector(`[data-product="${toIndex}"]`));
    const main     = /** @type {HTMLElement | null} */ (this.querySelector('[data-main]'));

    if (!outgoing || !incoming || !main) { this.#animating = false; return; }

    // ── Calculate FLIP: translate from main-center → thumb-circle-center ──

    const mainRect = main.getBoundingClientRect();
    const mainCX   = mainRect.left + mainRect.width  / 2;
    const mainCY   = mainRect.top  + mainRect.height / 2;

    // Outgoing: product shrinks toward its OWN thumbnail circle
    const fromCircle = this.querySelector(`[data-thumb-circle="${fromIndex}"]`);
    if (fromCircle) {
      const r     = fromCircle.getBoundingClientRect();
      const tx    = (r.left + r.width  / 2) - mainCX;
      const ty    = (r.top  + r.height / 2) - mainCY;
      const imgEl = outgoing.querySelector('.product-carousel__product-img');
      const scale = imgEl ? r.width / imgEl.getBoundingClientRect().width : 0.22;
      outgoing.style.setProperty('--leave-tx',    `${tx}px`);
      outgoing.style.setProperty('--leave-ty',    `${ty}px`);
      outgoing.style.setProperty('--leave-scale', `${scale}`);
    }

    // Incoming: product grows from its OWN thumbnail circle
    const toCircle = this.querySelector(`[data-thumb-circle="${toIndex}"]`);
    if (toCircle) {
      const r     = toCircle.getBoundingClientRect();
      const tx    = (r.left + r.width  / 2) - mainCX;
      const ty    = (r.top  + r.height / 2) - mainCY;
      const imgEl = outgoing.querySelector('.product-carousel__product-img');
      const scale = imgEl ? r.width / imgEl.getBoundingClientRect().width : 0.22;
      incoming.style.setProperty('--enter-tx',    `${tx}px`);
      incoming.style.setProperty('--enter-ty',    `${ty}px`);
      incoming.style.setProperty('--enter-scale', `${scale}`);
    }

    // ── Trigger animations ────────────────────────────────────────────────

    outgoing.classList.remove('is-active');
    outgoing.classList.add('is-leaving');
    outgoing.setAttribute('aria-hidden', 'true');

    incoming.classList.add('is-entering');
    incoming.setAttribute('aria-hidden', 'false');

    // Info panel cross-fade
    this.querySelector(`[data-info-panel="${fromIndex}"]`)?.classList.remove('is-active');
    this.querySelector(`[data-info-panel="${toIndex}"]`)?.classList.add('is-active');

    // Thumb active state
    this.querySelector(`[data-thumb="${fromIndex}"]`)?.classList.remove('is-active');
    this.querySelector(`[data-thumb="${fromIndex}"]`)?.setAttribute('aria-pressed', 'false');
    this.querySelector(`[data-thumb="${toIndex}"]`)?.classList.add('is-active');
    this.querySelector(`[data-thumb="${toIndex}"]`)?.setAttribute('aria-pressed', 'true');

    // ── Cleanup after CSS animation ends (350ms + small buffer) ──────────

    setTimeout(() => {
      outgoing.classList.remove('is-leaving');
      outgoing.style.removeProperty('--leave-tx');
      outgoing.style.removeProperty('--leave-ty');
      outgoing.style.removeProperty('--leave-scale');

      incoming.classList.remove('is-entering');
      incoming.classList.add('is-active');
      incoming.style.removeProperty('--enter-tx');
      incoming.style.removeProperty('--enter-ty');
      incoming.style.removeProperty('--enter-scale');

      this.#activeIndex = toIndex;
      this.#animating   = false;
    }, 380);
  }

  // ─── Background swap ─────────────────────────────────────────────────────

  #activateBg(/** @type {number} */ index) {
    this.querySelectorAll('.product-carousel__bg-img').forEach((img, i) => {
      img.classList.toggle('is-active', i === index);
    });
  }
}

customElements.define('product-carousel', ProductCarousel);
